import { createAnalytics } from 'Lib'
import { BodyFastAction, IRootState, history, rootReducer } from 'Reducers'
import { rootSaga } from 'Sagas'
import { createFirebaseService, createGTagService, initStripeService, createTaboolaService } from 'Services'
import { routerMiddleware } from 'connected-react-router'
import { useDispatch as _useDispatch } from 'react-redux'
import { applyMiddleware, compose, createStore } from 'redux'
import { persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import createSagaMiddleware from 'redux-saga'
import { createLogger } from 'redux-logger'
import { createSentryMiddleware } from 'Reducers/SentryMiddleware'
import { createAppsFlyerService } from 'Services/AppsFlyerService'
import { createSnapchatService } from 'Services/Snapchat.service'
import { initSignalsGatewayPixel } from 'Services/SignalsGateway.service'
import { initFacebookPixel } from 'Services/Facebook.service'
import { initTikTokPixel } from 'Services/TikTok.service'
import { initOpenAiAdsService } from 'Services/OpenAiAds.service'
import { handleSagaError } from 'Sagas/SagaUtils'

const REDUX_PERSIST_WHITELIST: Array<keyof IRootState> = ['payment', 'onboarding']

const REDUX_PERSIST_CONFIG = {
  key: '__REDUX_ROOT__',
  storage,
  whitelist: REDUX_PERSIST_WHITELIST
}

const persistedReducer = persistReducer(REDUX_PERSIST_CONFIG, rootReducer)

const sagaMiddleware = createSagaMiddleware({ onError: handleSagaError })

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

const middlewares = []
middlewares.push(routerMiddleware(history))
middlewares.push(sagaMiddleware)
if (__DEV__) {
  middlewares.push(createLogger({
    collapsed: true,
    diff: true
  }))
}

const sentryMiddleware = createSentryMiddleware()
middlewares.push(sentryMiddleware)

export const store = createStore<any, BodyFastAction, any, any>(
  persistedReducer,
  composeEnhancers(applyMiddleware(...middlewares))
)
sagaMiddleware.run(rootSaga)

export const persistor = persistStore(store)

initSignalsGatewayPixel()
initFacebookPixel(store)
// Only builds the queue shim — the TikTok SDK itself is loaded by
// grantTikTokConsent, so nothing reaches TikTok before targeting consent.
initTikTokPixel(store)
// Only builds the queue shim — the OpenAI SDK itself is loaded by
// grantOpenAiAdsConsent, so nothing reaches OpenAI before targeting consent.
initOpenAiAdsService(store)

export const gTagService = createGTagService()
export const firebaseServicePromise = createFirebaseService(store)

export const taboolaService = createTaboolaService()
export const snapchatService = createSnapchatService(store)
// export const pinterestService = createPinterestService()
export const stripeService = initStripeService(store)
export const appsFlyerService = createAppsFlyerService()

export const analytics = createAnalytics(gTagService, taboolaService, snapchatService) // Pinterest service is removed temporarily.

// Normally this is where we would initialize MoEngage
// However MoEngage is removed temporarily. Please see this slack chat for details: https://bodyfastworkspace.slack.com/archives/CDAJ9JY6T/p1733926070929709
// Also when it's time to revert please refer to this PR: https://github.com/matttti/BodyFast/pull/4496

export type StoreDispatch = (action: BodyFastAction) => void

export function useDispatch(): StoreDispatch {
  const dispatch = _useDispatch()
  return (action: BodyFastAction) => {
    dispatch(action)
  }
}
